import { ref, watchEffect, provide, inject } from 'vue'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  getColorScheme,
  checkPrefersIncreasedContrast,
} from '@storyblok/accessibility'
import { useUser } from '@/store/modules/App/user'
import type { SbTheme } from '@/components/ContentManager/AccountSettings/types'

export type Theme =
  | 'default'
  | 'dark'
  | 'light-high-contrast'
  | 'dark-high-contrast'

// Non-exported symbol for internal use only
const ThemeSymbol = Symbol('theme')

type ThemeState = {
  systemColorScheme: Ref<'light' | 'dark' | null>
  prefersContrast: Ref<boolean>
  theme: Ref<Theme>
}

let listenerCount = 0
let themeInitialized = false
let mqls: MediaQueryList[] | null = null
// Store state reference at module level so event listeners can access it
let globalState: ThemeState | null = null

const MEDIA_QUERIES = [
  '(prefers-color-scheme: dark)',
  '(prefers-color-scheme: light)',
  '(prefers-contrast: more)',
  '(prefers-contrast: no-preference)',
]

function getThemeState(): ThemeState {
  let state = inject<ThemeState | null>(ThemeSymbol, null)

  if (!state) {
    state = {
      systemColorScheme: ref<'light' | 'dark' | null>(getColorScheme()),
      prefersContrast: ref<boolean>(checkPrefersIncreasedContrast()),
      theme: ref<Theme>('default'),
    }
    provide(ThemeSymbol, state)
    globalState = state
  }

  return state
}

function updateSystemPreferences() {
  // Use globalState instead of getThemeState() since this runs outside Vue context
  if (!globalState) {
    return
  }
  globalState.systemColorScheme.value = getColorScheme()
  globalState.prefersContrast.value = checkPrefersIncreasedContrast()
}

function activateSystemPreferenceListeners() {
  if (!window?.matchMedia) {
    return
  }

  watchEffect((onCleanup) => {
    listenerCount++

    if (listenerCount === 1) {
      mqls = MEDIA_QUERIES.map((q) => window.matchMedia(q))
      mqls.forEach((mql) =>
        mql.addEventListener('change', updateSystemPreferences),
      )
    }

    onCleanup(() => {
      listenerCount--
      if (listenerCount === 0 && mqls) {
        mqls.forEach((mql) =>
          mql.removeEventListener('change', updateSystemPreferences),
        )
        mqls = null
      }
    })
  })
}

/**
 * Resolves the final theme based on user preference, system preferences, and high contrast settings.
 *
 * @param userTheme - The user's theme preference (can be undefined, 'sync-system', 'default', 'dark', or already resolved themes)
 * @param hasHighContrast - Whether the user prefers increased contrast
 * @param systemColorScheme - The system's color scheme preference ('light', 'dark', or null)
 * @returns The resolved theme
 */
export function resolveTheme(
  userTheme: SbTheme | undefined,
  hasHighContrast: boolean,
  systemColorScheme: 'light' | 'dark' | null,
): Theme {
  // Handle undefined or sync-system theme - derive from system preferences
  if (userTheme === undefined || userTheme === 'sync-system') {
    if (systemColorScheme === 'dark') {
      return hasHighContrast ? 'dark-high-contrast' : 'dark'
    } else {
      return hasHighContrast ? 'light-high-contrast' : 'default'
    }
  }
  // Handle explicit dark theme - apply high contrast if needed
  else if (userTheme === 'dark') {
    return hasHighContrast ? 'dark-high-contrast' : 'dark'
  }
  // Handle explicit default theme - apply high contrast if needed
  else if (userTheme === 'default') {
    return hasHighContrast ? 'light-high-contrast' : 'default'
  }
  // Already resolved theme (light-high-contrast or dark-high-contrast) - return as-is
  // TypeScript can now narrow this correctly
  if (
    userTheme === 'light-high-contrast' ||
    userTheme === 'dark-high-contrast'
  ) {
    return userTheme
  }

  return 'default'
}

function applyTheme(state: ThemeState, theme: Theme): void {
  state.theme.value = theme
  const currentTheme = document.documentElement.getAttribute('theme')
  if (currentTheme !== theme) {
    document.documentElement.setAttribute('theme', theme)
  }
}

function updateThemeFromUserPreference(
  state: ThemeState,
  getUserTheme: Ref<SbTheme | undefined>,
): void {
  // Force default theme for logged-out users.
  if (!getUserTheme.value) {
    applyTheme(state, 'default')
    return
  }

  const hasHighContrast = state.prefersContrast.value
  const resolvedTheme = resolveTheme(
    getUserTheme.value,
    hasHighContrast,
    state.systemColorScheme.value,
  )

  applyTheme(state, resolvedTheme)
}

function initThemeLogic(): void {
  if (themeInitialized) {
    return
  }
  themeInitialized = true

  const state = getThemeState()
  const user = useUser()
  const { getUserTheme } = storeToRefs(user)

  watchEffect(() => {
    updateThemeFromUserPreference(state, getUserTheme)
  })
}

// Provide theme state at the root component
// Call this once at the app root to initialize theme system
export function useProvideTheme(): void {
  activateSystemPreferenceListeners()
  initThemeLogic()
}

// Composable to access theme in components
// Must be called within a component setup function
export function useTheme(): Ref<Theme> {
  const state = getThemeState()
  initThemeLogic()
  return state.theme
}
